'use client';

import { useMemo, useCallback, useState, type ReactNode } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { IconMoodEmpty } from '@tabler/icons-react';
import {
  GPU_PRODUCTS,
  REGION_OPTIONS,
  PRICE_RANGE,
  getAvailableGPUs,
  getComingSoonGPUs,
  getRegionalPrice,
  getRegionName,
  type GPUProduct,
} from '@/lib/data/gpu-products';
import { isInstanceAvailableInRegion, type AirGPURegion } from '@/lib/integrations/airgpu/types';
import { formatHourlyRate } from '@/lib/utils/currency';
import { Button } from '@/components/ui/button';
import { GPUHeroSection } from './gpu-hero-section';
import { GPUFilterBar, type GPUFilters } from './gpu-filter-bar';
import { GPUProductCard, GPUProductCardCompact } from './gpu-product-card';
import { GPULegendaryDrop } from './gpu-legendary-drop';
import { GPUPowerBattle } from './gpu-power-battle';
import { GPUPowerUps } from './gpu-power-ups';
import { Reveal } from './arcade-art';
import { ARCADE_CLASSES, inClass, sortGpus, type ArcadeClassId, type SortId } from './arcade-classes';
import { machineCreateHref, productsHref } from '@/lib/catalog/filters';

const NO_FILTERS: GPUFilters = {
  gpuType: null,
  region: null,
  priceRange: [PRICE_RANGE.min, PRICE_RANGE.max],
  showComingSoon: true,
};

const PAGE = 'mx-auto w-full max-w-[1440px] px-4 sm:px-8 lg:px-12';

function SectionHeading({
  id,
  eyebrow,
  title,
  glow,
  children,
}: {
  id: string;
  eyebrow: string;
  title: string;
  glow: string;
  children?: ReactNode;
}) {
  return (
    <div className="mb-7 flex flex-col gap-3 lg:mb-9 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
          <span className="h-2 w-2 rounded-[2px] bg-primary shadow-[0_0_8px_var(--primary)]" />
          {eyebrow}
        </p>
        <h2
          id={id}
          className="mt-2 font-heading text-4xl font-bold uppercase leading-[0.95] tracking-tight text-foreground sm:text-5xl lg:text-6xl"
        >
          {title} <span className="ga-headline-glow">{glow}</span>
        </h2>
      </div>
      {children && <div className="max-w-md text-sm leading-relaxed text-muted-foreground lg:text-right">{children}</div>}
    </div>
  );
}

export function GPUProductsPageClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Class and sort are view state on this page; the shareable filters
  // (GPU, region, price) live in the URL.
  const [activeClass, setActiveClass] = useState<ArcadeClassId>('all');
  const [sort, setSort] = useState<SortId>('featured');

  // Parse filters from URL
  const filters: GPUFilters = useMemo(() => {
    const gpuType = searchParams.get('gpu');
    const region = searchParams.get('region') as AirGPURegion | null;
    const priceMin = parseFloat(searchParams.get('priceMin') || String(PRICE_RANGE.min));
    const priceMax = parseFloat(searchParams.get('priceMax') || String(PRICE_RANGE.max));

    return {
      gpuType: gpuType && GPU_PRODUCTS.some((g) => g.id === gpuType) ? gpuType : null,
      region: region && REGION_OPTIONS.some((r) => r.id === region) ? region : null,
      priceRange: [
        isNaN(priceMin) ? PRICE_RANGE.min : Math.max(PRICE_RANGE.min, priceMin),
        isNaN(priceMax) ? PRICE_RANGE.max : Math.min(PRICE_RANGE.max, priceMax),
      ] as [number, number],
      showComingSoon: searchParams.get('showComingSoon') !== 'false',
    };
  }, [searchParams]);

  // Update URL when filters change
  const updateFilters = useCallback(
    (newFilters: GPUFilters) => {
      // Built by the same productsHref() every link on the site uses, so the
      // address bar after filtering and the href that would have produced this
      // view are the same string.
      router.push(
        productsHref({
          gpu: newFilters.gpuType || undefined,
          region: newFilters.region || undefined,
          priceMin:
            newFilters.priceRange[0] !== PRICE_RANGE.min
              ? newFilters.priceRange[0]
              : undefined,
          priceMax:
            newFilters.priceRange[1] !== PRICE_RANGE.max
              ? newFilters.priceRange[1]
              : undefined,
        }),
        { scroll: false },
      );
    },
    [router]
  );

  const clearAll = useCallback(() => {
    setActiveClass('all');
    setSort('featured');
    updateFilters(NO_FILTERS);
  }, [updateFilters]);

  // Filter products by the URL filters
  const filteredProducts = useMemo(() => {
    return GPU_PRODUCTS.filter((gpu) => {
      if (filters.gpuType && gpu.id !== filters.gpuType) {
        return false;
      }

      // The slider works on base rates (it says so), so a region pick does not
      // move a card in or out of the price window.
      if (gpu.basePrice < filters.priceRange[0] || gpu.basePrice > filters.priceRange[1]) {
        return false;
      }

      // A region filter shows what can actually be created there. Without
      // this the create form would silently swap the region for the first one
      // that offers the GPU.
      if (filters.region) {
        if (!gpu.instanceType || !isInstanceAvailableInRegion(gpu.instanceType, filters.region)) {
          return false;
        }
      }

      if (!filters.showComingSoon && !gpu.available) {
        return false;
      }

      return true;
    });
  }, [filters]);

  const priceOf = useCallback(
    (gpu: GPUProduct) => (filters.region ? getRegionalPrice(gpu, filters.region) : gpu.basePrice),
    [filters.region]
  );
  const priceLabel = filters.region ? getRegionName(filters.region) : 'From';

  // Per-class counts under the URL filters, then the class and the sort.
  const classCounts = useMemo(
    () =>
      Object.fromEntries(
        ARCADE_CLASSES.map((cls) => [cls.id, filteredProducts.filter((gpu) => inClass(gpu, cls.id)).length])
      ) as Record<ArcadeClassId, number>,
    [filteredProducts]
  );
  const visibleProducts = useMemo(
    () => sortGpus(filteredProducts.filter((gpu) => inClass(gpu, activeClass)), sort, priceOf),
    [filteredProducts, activeClass, sort, priceOf]
  );

  // Create-form link for a product, carrying the region filter when set.
  const rentHref = useCallback(
    (gpu: GPUProduct) =>
      gpu.available && gpu.instanceType
        ? machineCreateHref({ instanceType: gpu.instanceType, region: filters.region ?? undefined })
        : undefined,
    [filters.region]
  );

  // Stats
  const stats = useMemo(() => {
    const availableGPUs = getAvailableGPUs();
    return {
      totalGPUs: availableGPUs.length,
      totalRegions: REGION_OPTIONS.length,
      lowestPrice: Math.min(...availableGPUs.map((g) => g.basePrice)),
      // Not yet on sale (withdrawn products are not counted).
      comingSoon: getComingSoonGPUs().length,
    };
  }, []);

  // Featured loadout: the popular size, or the first rentable one, among
  // those that can be created in the selected region.
  const featured = useMemo(() => {
    const candidates = getAvailableGPUs().filter(
      (gpu) =>
        !filters.region || (gpu.instanceType && isInstanceAvailableInRegion(gpu.instanceType, filters.region))
    );
    return candidates.find((g) => g.badge === 'Popular') ?? candidates[0];
  }, [filters.region]);
  const featuredHref = featured ? rentHref(featured) : undefined;
  const showFeatured = !!featured && !!featuredHref && !filters.gpuType;

  // The compare widget keeps every rentable GPU selectable, so it prices at
  // the "from" rate and only carries the region where that GPU is offered.
  const battleHref = useCallback(
    (gpu: GPUProduct) =>
      gpu.instanceType
        ? machineCreateHref({
            instanceType: gpu.instanceType,
            region:
              filters.region && isInstanceAvailableInRegion(gpu.instanceType, filters.region)
                ? filters.region
                : undefined,
          })
        : undefined,
    [filters.region]
  );
  const basePrice = useCallback((gpu: GPUProduct) => gpu.basePrice, []);

  const renderCard = (gpu: GPUProduct, position: number) => (
    <Reveal key={gpu.id} index={position % 3} className="h-full">
      <GPUProductCard
        gpu={gpu}
        index={GPU_PRODUCTS.indexOf(gpu)}
        rentHref={rentHref(gpu)}
        price={priceOf(gpu)}
        priceLabel={priceLabel}
      />
    </Reveal>
  );

  const firstRow = visibleProducts.slice(0, 3);
  const rest = visibleProducts.slice(3);
  const legendary =
    showFeatured && featured && featuredHref ? (
      <Reveal className="my-8 lg:my-10">
        <GPULegendaryDrop
          gpu={featured}
          index={GPU_PRODUCTS.indexOf(featured)}
          href={featuredHref}
          price={priceOf(featured)}
          priceLabel={priceLabel}
        />
      </Reveal>
    ) : null;

  return (
    <div className="ga-space min-h-screen text-foreground">
      <GPUHeroSection
        totalGPUs={stats.totalGPUs}
        totalRegions={stats.totalRegions}
        lowestPrice={stats.lowestPrice}
      />

      {/* Build your loadout */}
      <section id="loadout" aria-labelledby="loadout-title" className="relative scroll-mt-24 py-14 lg:py-20">
        <div aria-hidden className="constellation-grid pointer-events-none absolute inset-0 opacity-40" />
        <div className={`${PAGE} relative`}>
          <SectionHeading id="loadout-title" eyebrow="Equipment shop" title="Build your" glow="loadout">
            {filters.region
              ? `Rates in ${getRegionName(filters.region)}, before upload bandwidth and extra disk.`
              : `From ${formatHourlyRate(stats.lowestPrice)}. The region and upload bandwidth can raise the rate; the configurator shows it before you create.`}
            {stats.comingSoon > 0 && ` ${stats.comingSoon} more coming soon.`}
          </SectionHeading>

          <GPUFilterBar
            filters={filters}
            onFiltersChange={updateFilters}
            resultCount={visibleProducts.filter((gpu) => gpu.available).length}
            activeClass={activeClass}
            onClassChange={setActiveClass}
            classCounts={classCounts}
            sort={sort}
            onSortChange={setSort}
            onClearAll={clearAll}
          />

          <div className="mt-8">
            {visibleProducts.length === 0 ? (
              <>
                <div className="ga-console rounded-2xl px-6 py-12 text-center">
                  <IconMoodEmpty className="mx-auto size-10 text-accent" aria-hidden />
                  <h3 className="mt-3 font-heading text-2xl font-bold text-foreground">No GPUs in this combo</h3>
                  <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                    {filters.region
                      ? 'No GPU in this class and price range is offered in that region.'
                      : 'Nothing matches this class and these filters. Try another class or clear the filters.'}
                  </p>
                  <Button onClick={clearAll} className="arcade-press mt-6 h-11 rounded-xl px-6 font-bold">
                    Clear filters
                  </Button>
                  <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-3 text-left sm:grid-cols-2">
                    {getAvailableGPUs()
                      .slice(0, 4)
                      .map((gpu) => (
                        <GPUProductCardCompact
                          key={gpu.id}
                          gpu={gpu}
                          index={GPU_PRODUCTS.indexOf(gpu)}
                          rentHref={machineCreateHref({ instanceType: gpu.instanceType ?? undefined })}
                        />
                      ))}
                  </div>
                </div>
                {legendary}
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3 xl:gap-6">
                  {firstRow.map(renderCard)}
                </div>
                {legendary ?? (rest.length > 0 && <div className="h-5 xl:h-6" />)}
                {rest.length > 0 && (
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3 xl:gap-6">
                    {rest.map((gpu, i) => renderCard(gpu, i + 3))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Power battle */}
      <section id="battle" aria-labelledby="battle-title" className="relative isolate scroll-mt-24 py-14 lg:py-24">
        <div aria-hidden className="shape-cut-reverse absolute inset-0 -z-10 bg-secondary/50" />
        <div aria-hidden className="ga-pixel-grid absolute inset-0 -z-10 opacity-60" />
        <div className={PAGE}>
          <SectionHeading id="battle-title" eyebrow="Quick compare" title="Power" glow="battle">
            Pick any two GPUs and put them head to head on VRAM, vCPUs, RAM, hourly rate and what they are
            recommended for.
          </SectionHeading>
          <Reveal>
            <GPUPowerBattle priceOf={basePrice} priceLabel="From" hrefOf={battleHref} />
          </Reveal>
        </div>
      </section>

      {/* Three power-ups */}
      <section
        id="power-ups"
        aria-labelledby="power-ups-title"
        className="relative scroll-mt-24 pb-20 pt-14 lg:pb-24 lg:pt-20"
      >
        <div className={PAGE}>
          <SectionHeading id="power-ups-title" eyebrow="How it works" title="Three" glow="power-ups">
            From picking a GPU to sitting at your cloud PC in three steps.
          </SectionHeading>
          <GPUPowerUps />
        </div>
      </section>
    </div>
  );
}
